/**
 * Per-user WalletConnect v2 session persistence.
 *
 * WC v2 stores its relay sessions in IndexedDB under the database name
 * `WALLET_CONNECT_V2_INDEXED_DB` (keyvaluestorage store).  When the same
 * browser is shared by multiple users we need to:
 *   1. Snapshot each user's WC session to localStorage on logout.
 *   2. Restore the correct snapshot when they log back in, even after
 *      another user has used the browser in the meantime.
 *
 * This avoids the "reconnect after logout" UX problem while keeping
 * cross-user wallet leakage impossible.
 */

export const WC_IDB_NAME = 'WALLET_CONNECT_V2_INDEXED_DB';
export const WC_STORE_NAME = 'keyvaluestorage';

/** localStorage key prefix — intentionally avoids `privy:` / `walletconnect` to survive purge. */
const WC_SAVE_PREFIX = '__pm_wc_';

type WCEntry = [key: string, value: string];

/** Read every key-value entry from the WC v2 IndexedDB. */
async function readWCIDB(): Promise<WCEntry[]> {
  return new Promise((resolve) => {
    const open = indexedDB.open(WC_IDB_NAME);
    open.onerror = () => resolve([]);
    open.onsuccess = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(WC_STORE_NAME)) {
        db.close();
        resolve([]);
        return;
      }
      const entries: WCEntry[] = [];
      const tx = db.transaction(WC_STORE_NAME, 'readonly');
      const cursorReq = tx.objectStore(WC_STORE_NAME).openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          entries.push([cursor.key as string, cursor.value as string]);
          cursor.continue();
        } else {
          db.close();
          resolve(entries);
        }
      };
      cursorReq.onerror = () => {
        db.close();
        resolve(entries);
      };
    };
  });
}

/** Delete the WC v2 IndexedDB entirely. */
export async function deleteWCIDB(): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(WC_IDB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

/**
 * Snapshot the current WC session to localStorage under the given user ID.
 * Call this during logout so the session can be restored on re-login.
 */
export async function saveWCSessionForUser(userId: string): Promise<void> {
  try {
    const entries = await readWCIDB();
    if (!entries.length) return;
    localStorage.setItem(`${WC_SAVE_PREFIX}${userId}`, JSON.stringify(entries));
  } catch (err) {
    console.warn('[WCSession] Failed to save session:', err);
  }
}

/**
 * Restore a previously-saved WC session for `userId` into the WC IndexedDB,
 * replacing whatever is there now.
 *
 * Returns `true` if a saved session was found and written, `false` otherwise.
 * Either way the caller should `window.location.reload()` afterwards so that
 * the WC client starts fresh from the correct IDB state.
 */
export async function restoreWCSessionForUser(userId: string): Promise<boolean> {
  const raw = localStorage.getItem(`${WC_SAVE_PREFIX}${userId}`);
  if (!raw) return false;

  let entries: WCEntry[];
  try {
    entries = JSON.parse(raw) as WCEntry[];
  } catch {
    localStorage.removeItem(`${WC_SAVE_PREFIX}${userId}`);
    return false;
  }

  // Wipe whatever is in the IDB now (previous user's session).
  await deleteWCIDB();

  if (!entries.length) return false;

  await new Promise<void>((resolve) => {
    const open = indexedDB.open(WC_IDB_NAME);
    open.onerror = () => resolve();
    open.onupgradeneeded = () => {
      if (!open.result.objectStoreNames.contains(WC_STORE_NAME)) {
        open.result.createObjectStore(WC_STORE_NAME);
      }
    };
    open.onsuccess = () => {
      const db = open.result;
      const tx = db.transaction(WC_STORE_NAME, 'readwrite');
      const store = tx.objectStore(WC_STORE_NAME);
      for (const [k, v] of entries) store.put(v, k);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        resolve();
      };
    };
  });

  return true;
}
